import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  LogOut,
  FileDown,
  ChevronRight,
  Inbox,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sp_admin_token";

const FIELD_LABELS = {
  existing_website_url: "Existing Website",
  domain_owned: "Owns a Domain",
  domain_details: "Domain & Registrar",
  primary_objective: "Primary Objective",
  primary_objective_other: "Objective (Other)",
  primary_cta: "Primary CTA Text",
  local_url_1: "Local Competitor 1",
  local_url_2: "Local Competitor 2",
  local_notes: "Local Competitor Notes",
  national_url_1: "Industry Leader 1",
  national_url_2: "Industry Leader 2",
  national_admire: "Admired Elements",
  design_inspiration: "Design Inspiration",
  business_name: "Business Name",
  tagline: "Tagline",
  brand_colors: "Brand Colors",
  aesthetic_vibe: "Aesthetic Vibe",
  aesthetic_vibe_other: "Vibe (Other)",
  has_logo: "Has Logo",
  logo_business_name: "Logo Business Name",
  logo_tagline: "Logo Tagline",
  logo_style: "Logo Style",
  about_headline: "About Headline",
  bio: "Bio / Founder Story",
  milestones: "Milestones",
  differentiators: "Differentiators",
  service_1_title: "Service 1",
  service_1_desc: "Service 1 Description",
  service_2_title: "Service 2",
  service_2_desc: "Service 2 Description",
  service_3_title: "Service 3",
  service_3_desc: "Service 3 Description",
  service_4_title: "Service 4",
  service_4_desc: "Service 4 Description",
  proof_options: "Proof Sections",
  step_1: "Step 1",
  step_2: "Step 2",
  step_3: "Step 3",
  portfolio_link: "Portfolio Folder",
  review_1_quote: "Review 1",
  review_1_name: "Review 1 Client",
  review_1_meta: "Review 1 Project",
  review_2_quote: "Review 2",
  review_2_name: "Review 2 Client",
  review_2_meta: "Review 2 Project",
  review_3_quote: "Review 3",
  review_3_name: "Review 3 Client",
  review_3_meta: "Review 3 Project",
  contact_form_fields: "Contact Form Fields",
  lead_emails: "Lead Notification Emails",
  integrations: "Integrations",
  phone: "Phone",
  business_email: "Business Email",
  address_area: "Address / Service Area",
  hours: "Hours",
  social_links: "Social Links",
  contact_name: "Contact Person",
  contact_email: "Contact Email",
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [briefs, setBriefs] = useState(null);
  const [active, setActive] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBriefs(null);
    setActive(null);
  }, []);

  const loadBriefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/website-briefs`, { headers: authHeaders() });
      setBriefs(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (token) loadBriefs();
  }, [token, loadBriefs]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Login failed. Try again."
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const openDetail = async (id) => {
    if (active?.id === id) {
      setActive(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/admin/website-briefs/${id}`, { headers: authHeaders() });
      setActive(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoadingDetail(false);
    }
  };

  const downloadFile = async (briefId, stored, name) => {
    try {
      const resp = await axios.get(`${API}/admin/website-briefs/${briefId}/files/${stored}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B] px-5">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#131316] p-8" data-testid="admin-login-card">
          <div className="mb-6 flex items-center gap-3">
            <img src="/assets/sparkplug-logo.webp" alt="Spark Plug" className="h-8 w-auto" />
          </div>
          <div className="mb-1 flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#D9A92E]" />
            <h1 className="font-display text-xl font-bold text-white">Admin Login</h1>
          </div>
          <p className="mb-6 text-sm text-zinc-500">Website briefs &amp; attachments dashboard.</p>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              className="spark-input"
              data-testid="admin-username-input"
            />
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="spark-input"
              data-testid="admin-password-input"
            />
            {loginError && (
              <p className="text-sm text-red-400" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D9A92E] px-6 py-3 text-sm font-semibold text-[#09090B] transition-colors hover:bg-[#E8C05A] disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              {loggingIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] px-5 py-10 sm:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/sparkplug-logo.webp" alt="Spark Plug" className="h-9 w-auto" />
            <div>
              <h1 className="font-display text-xl font-bold text-white">Website Briefs</h1>
              <p className="text-xs text-zinc-500">Submissions from the development form</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-400 transition-colors hover:border-white/30 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        {briefs === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#D9A92E]" />
          </div>
        ) : briefs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] py-20 text-center" data-testid="admin-empty-state">
            <Inbox className="h-8 w-8 text-zinc-600" />
            <p className="text-sm text-zinc-500">No briefs yet. New submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-briefs-list">
            {briefs.map((b) => (
              <div key={b.id} className="overflow-hidden rounded-2xl border border-white/10 bg-[#131316]">
                <button
                  onClick={() => openDetail(b.id)}
                  data-testid={`admin-brief-row-${b.id}`}
                  className="flex w-full flex-wrap items-center gap-x-6 gap-y-1 px-6 py-4 text-left transition-colors hover:bg-white/[0.03]"
                >
                  <span className="min-w-[160px] text-sm font-semibold text-white">
                    {b.business_name || "—"}
                  </span>
                  <span className="text-sm text-zinc-400">{b.contact_name}</span>
                  <span className="text-sm text-zinc-500">{b.contact_email}</span>
                  <span className="mono ml-auto text-[11px] uppercase tracking-widest text-zinc-600">
                    {new Date(b.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {" · "}
                    {(b.files || []).length} file{(b.files || []).length === 1 ? "" : "s"}
                  </span>
                  <ChevronRight
                    className={`h-4 w-4 text-zinc-500 transition-transform ${
                      active?.id === b.id ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {active?.id === b.id && (
                  <div className="border-t border-white/10 px-6 py-6" data-testid={`admin-brief-detail-${b.id}`}>
                    {loadingDetail ? (
                      <Loader2 className="h-5 w-5 animate-spin text-[#D9A92E]" />
                    ) : (
                      <>
                        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                          {Object.entries(active.data || {})
                            .filter(([, v]) => v !== "" && v !== null && !(Array.isArray(v) && !v.length))
                            .map(([k, v]) => (
                              <div key={k}>
                                <dt className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                                  {FIELD_LABELS[k] || k.replace(/_/g, " ")}
                                </dt>
                                <dd className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">
                                  {Array.isArray(v) ? v.join(", ") : String(v)}
                                </dd>
                              </div>
                            ))}
                        </dl>
                        {(active.files || []).length > 0 && (
                          <div className="mt-6 border-t border-white/10 pt-5">
                            <p className="mono mb-3 text-[11px] uppercase tracking-widest text-zinc-600">
                              Attachments
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {active.files.map((f) => (
                                <button
                                  key={f.stored}
                                  onClick={() => downloadFile(active.id, f.stored, f.name)}
                                  data-testid={`admin-file-download-${f.stored}`}
                                  className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-zinc-300 transition-colors hover:border-[#D9A92E]/60 hover:text-white"
                                >
                                  <FileDown className="h-3.5 w-3.5 text-[#D9A92E]" />
                                  {f.name}
                                  <span className="text-zinc-600">({Math.round(f.size / 1024)} KB)</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
